import React, { useState, useMemo, useEffect } from 'react';
import { Container, Grid, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Import newly created components
import FilterRowTop from './Components/FilterRowTop';
import FilterRowMiddle from './Components/FilterRowMiddle';
import RecordListPanel from './Components/RecordListPanel';
import RecordContentPanel from './Components/RecordContentPanel';

// --- Configuration Constant ---
const RECORDS_PER_PAGE = 25;

// --- Utility Functions ---

/**
 * Parses the 'record' URL parameter and extracts the year and full ID.
 * Example: '25-1' -> { shortYear: '25', fullId: '25-1', fullYear: 2025 }
 * @returns {{shortYear: string|null, fullId: string|null, fullYear: number|null}}
 */
const parseUrlRecordParam = () => {
    const params = new URLSearchParams(window.location.search);
    const recordParam = params.get('record'); // e.g., '25-1'

    if (recordParam && recordParam.match(/^\d{2}-\d+$/)) {
        const shortYear = recordParam.split('-')[0]; // '25'
        const fullYear = 2000 + parseInt(shortYear, 10); // 2025
        
        // Basic sanity check for year range, e.g., '25' -> 2025
        if (fullYear >= 2000 && fullYear <= 2100) {
            return {
                shortYear: shortYear,
                fullId: recordParam,
                fullYear: fullYear,
            };
        }
    }
    return { shortYear: null, fullId: null, fullYear: null };
};


const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= 2017; i--) { 
    years.push(i);
  }
  return years;
};

const initialDateRange = (year) => {
  const yearInt = parseInt(year);
  return {
    startDate: new Date(yearInt, 0, 1), 
    endDate: new Date(yearInt, 11, 31), 
  };
};

const getUniqueDepartments = (data) => {
  if (!data || !data.filters || !data.filters.departments) return [];
  return Object.keys(data.filters.departments).filter(dep => dep !== 'null');
};

const EMPTY_DATA = { records: [], filters: { status: {}, date: {}, departments: {}, documents: {} }, invertedIndex: {} };

// --- Main Component ---
const RecordSearchApp = () => {
  // --- URL Parsing and Initial Default Setup ---
  const urlParams = useMemo(() => parseUrlRecordParam(), []); // Parse URL once on mount
  
  // Set defaultYear based on URL param or current year/default
  const currentYear = new Date().getFullYear();
  // If URL has a valid record ID (e.g., 25-1 -> 2025), use that year, otherwise use the current year.
  const defaultYear = urlParams.fullYear || currentYear; 

  const yearOptions = getYearOptions();

  // State for List Data and Loading
  const [data, setData] = useState(EMPTY_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // 🌟 NEW STATE for Selected Record Content and Loading 🌟
  const [selectedRecordContent, setSelectedRecordContent] = useState(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [contentError, setContentError] = useState(null);

  // State for Filters
  // Use URL param's year if available, otherwise use the calculated defaultYear
  const [selectedYear, setSelectedYear] = useState(defaultYear); 
  const [dateRange, setDateRange] = useState(initialDateRange(defaultYear));
  const [statusFilter, setStatusFilter] = useState('Both');
  const [documentCountFilter, setDocumentCountFilter] = useState('Any');
  const [searchText, setSearchText] = useState(''); 
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [suggestions, setSuggestions] = useState([]); 
  const [searchKeywords, setSearchKeywords] = useState([]); 
  const [inputValue, setInputValue] = useState('');

  // State for Sorting and Display
  const [sortKey, setSortKey] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');
  // Use URL param's full ID (e.g., '25-1') if available, otherwise use a fallback ID based on the default year.
  const defaultRecordId = urlParams.fullId || `${String(defaultYear).slice(-2)}-1`;
  const [selectedRecordId, setSelectedRecordId] = useState(defaultRecordId);

  // --- Pagination State and Handler ---
  const [currentPage, setCurrentPage] = useState(1);
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    const listElement = document.getElementById('record-list-container');
    if (listElement) listElement.scrollTop = 0;
  };
  
// --- 1. Master List Data Fetching Logic (useEffect) ---
  useEffect(() => {
    const fetchData = async () => {
        const yearSuffix = String(selectedYear).slice(-2);
        const url = `https://content.firerebuild.com/records/lacity-data/search/search-${yearSuffix}.json`;
        
        setIsLoading(true);
        setError(null);
        setData(EMPTY_DATA); 
        // Do NOT reset selectedRecordId here, let the content useEffect handle it
        
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Failed to load data for ${selectedYear}. Status: ${response.status}`);
            }
            let json = await response.json();
            
            // --- Data Restructuring and Formatting ---
            const formattedRecords = {};
            
            // 1. Process Status Filter
            if (json.filters && json.filters.status) {
                for (const status in json.filters.status) {
                    const recordIds = json.filters.status[status];
                    recordIds.forEach(id => {
                        formattedRecords[id] = formattedRecords[id] || { id };
                        formattedRecords[id].status = status;
                    });
                }
            }
            
            // 2. Process Date Filter
            if (json.filters && json.filters.date) {
                for (const date in json.filters.date) {
                    const recordIds = json.filters.date[date];
                    recordIds.forEach(id => {
                        formattedRecords[id] = formattedRecords[id] || { id };
                        formattedRecords[id].date = date;
                    });
                }
            }
            
            // 3. Process Department Filter
            if (json.filters && json.filters.departments) {
                for (const department in json.filters.departments) {
                    const recordIds = json.filters.departments[department];
                    recordIds.forEach(id => {
                        formattedRecords[id] = formattedRecords[id] || { id };
                        formattedRecords[id].department = department;
                    });
                }
            }
            
            // 4. Process Documents Count Filter (NEW)
            if (json.filters && json.filters.documents) {
                for (const count in json.filters.documents) {
                    const recordIds = json.filters.documents[count];
                    recordIds.forEach(id => {
                        formattedRecords[id] = formattedRecords[id] || { id };
                        // Store the document count as a number
                        formattedRecords[id].documentCount = parseInt(count, 10); 
                    });
                }
            }


            // Finalize records array with default values and document count
            json.records = Object.values(formattedRecords).map(record => ({
                ...record,
                status: record.status || 'Unknown',
                date: record.date || 'N/A',
                department: record.department || 'N/A',
                // Ensure documentCount is a number, defaulting to 0
                documentCount: record.documentCount === undefined ? 0 : record.documentCount, 
            }));
            
            setData(json);
            
            // Set the first record as selected default if a new year is loaded and records exist
            // BUT ONLY if the current selectedRecordId doesn't belong to the newly loaded year
            const recordIdYear = selectedRecordId.split('-')[0];
            if (String(selectedYear).slice(-2) !== recordIdYear) {
                 if (json.records && json.records.length > 0) {
                    setSelectedRecordId(json.records[0].id);
                }
            }
            
        } catch (e) {
            console.error("Data fetching error:", e);
            setError(e.message || "An unknown error occurred while fetching data.");
        } finally {
            setIsLoading(false);
        }
    };

    fetchData();
    
    // Reset filters/range/pagination when the year changes
    setDateRange(initialDateRange(selectedYear));
    setStatusFilter('Both');
    setDocumentCountFilter('Any'); // Reset new filter
    setSearchText('');
    setSelectedDepartments([]);
    setCurrentPage(1);

  }, [selectedYear]);

// --- 2. Record Content Fetching Logic (useEffect) ---
  useEffect(() => {
    // Only fetch if a valid ID is selected
    if (!selectedRecordId) {
      setSelectedRecordContent(null);
      return;
    }
    
    var yearSuffix = String(selectedYear).slice(-2);
    var tempYear = selectedRecordId.split('-')[0];
    if (yearSuffix != tempYear) {
      return;
    }

    const fetchContent = async () => {
      const yearSuffix = String(selectedYear).slice(-2);
      const url = `https://content.firerebuild.com/records/lacity-data/requests/${yearSuffix}/${selectedRecordId}.json`;
      
      setIsContentLoading(true);
      setContentError(null);
      setSelectedRecordContent(null);

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to load content for ID: ${selectedRecordId}. Status: ${response.status}`);
        }
        const contentJson = await response.json();
        
        // Merge the basic record info (including documentCount) with the new content
        const basicInfo = data.records.find(r => r.id === selectedRecordId);
        
        setSelectedRecordContent({
          ...basicInfo, // Includes date, status, dept, and documentCount
          ...contentJson, // Includes the content field
          id: selectedRecordId // Ensure ID is correct
        });

      } catch (e) {
        console.error("Content fetching error:", e);
        setContentError(e.message || "An unknown error occurred while fetching content.");
      } finally {
        setIsContentLoading(false);
      }
    };

    fetchContent();
    
  }, [selectedRecordId, selectedYear, data.records]); // Depend on data.records to ensure basic info is available

  // Handle filter changes by resetting to page 1 (useEffect)
  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, statusFilter, documentCountFilter, searchKeywords, selectedDepartments, sortKey, sortDirection]);

  // --- Suggestion Logic (useEffect) ---
  useEffect(() => {
      // ... (Existing Suggestion Logic) ...
      const minLength = 3;
      const currentText = inputValue.toLowerCase().trim();

      if (currentText.length < minLength || !data.invertedIndex) {
          setSuggestions([]);
          return;
      }

      const matchingTerms = Object.keys(data.invertedIndex)
          .filter(term => term.startsWith(currentText));

      setSuggestions(matchingTerms.slice(0, 50));

  }, [inputValue, data.invertedIndex]);

  // --- Filter and Sort Logic (useMemo) ---
  const allFilteredRecords = useMemo(() => {
    if (isLoading || !data.records.length) return [];

    let records = data.records;
    const start = dateRange.startDate.getTime();
    const end = dateRange.endDate.getTime();

    // 1. Date Range Filter
    records = records.filter(record => {
      const recordDate = new Date(record.date).getTime();
      return recordDate >= start && recordDate <= end;
    });

    // 2. Status Filter
    if (statusFilter !== 'Both') { 
      records = records.filter(record => record.status === statusFilter);
    }
    
    // 3. Document Count Filter (NEW)
    if (documentCountFilter === 'No') {
        records = records.filter(record => record.documentCount === 0);
    } else if (documentCountFilter === 'Yes') {
        records = records.filter(record => record.documentCount > 0);
    }

    // 4. Department Filter
    if (selectedDepartments.length > 0) {
      records = records.filter(record => selectedDepartments.includes(record.department));
    }
    
    // 5. AND Keyword Search
    if (searchKeywords.length > 0) {
      // Convert all keywords to a standardized format (lowercase, trimmed)
      const terms = searchKeywords.map(keyword => keyword.toLowerCase().trim()).filter(term => term.length > 0);
      
      if (terms.length === 0) return [];
      
      let intersectingIds = new Set(data.invertedIndex[terms[0]] || []);
      
      if (intersectingIds.size === 0) return [];

      for (let i = 1; i < terms.length; i++) {
        const currentTerm = terms[i];
        const currentIds = data.invertedIndex[currentTerm] || [];
        const currentIdsSet = new Set(currentIds);

        intersectingIds = new Set(
          [...intersectingIds].filter(id => currentIdsSet.has(id))
        );

        if (intersectingIds.size === 0) break;
      }

      records = records.filter(record => intersectingIds.has(record.id));
    }

    // 6. Sorting
    records.sort((a, b) => {
      let comparison = 0;
      const dir = sortDirection === 'asc' ? 1 : -1;
      if (sortKey === 'date') {
        comparison = new Date(a.date) - new Date(b.date);
      } else if (sortKey === 'status') {
        comparison = a.status.localeCompare(b.status); 
      } else if (sortKey === 'documentCount') { // Allow sorting by document count
        comparison = a.documentCount - b.documentCount;
      }
      return comparison * dir;
    });

    return records;
  }, [data, dateRange, statusFilter, documentCountFilter, searchKeywords, selectedDepartments, sortKey, sortDirection, isLoading]);

  // --- Pagination Logic (useMemo) ---
  const pageCount = Math.ceil(allFilteredRecords.length / RECORDS_PER_PAGE);
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * RECORDS_PER_PAGE;
    const endIndex = startIndex + RECORDS_PER_PAGE;
    return allFilteredRecords.slice(startIndex, endIndex);
  }, [allFilteredRecords, currentPage]);

  // --- Sorting Handler ---
  const toggleSortDirection = (key) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };
  
  const allDepartments = useMemo(() => getUniqueDepartments(data), [data]);

  // --- Rendering UI ---
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Container
          maxWidth={false}
          sx={{
            mt: 4,
            width: '1160px',
            p: 2,
            mx: 'auto',
          }}
        >
          <Grid container spacing={2} sx={{ width: '1160px', mx: 'auto' }}>
            
            {/* --- TOP ROW: Year, Date Range, Status, Document Count (New) --- */}
            <Grid item xs={12}>
              <FilterRowTop
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                yearOptions={yearOptions}
                dateRange={dateRange}
                setDateRange={setDateRange}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                documentCountFilter={documentCountFilter}
                setDocumentCountFilter={setDocumentCountFilter}
              />
            </Grid>

            {/* --- MIDDLE ROW: Search Bar, Department Multi-Select --- */}
            <Grid item xs={12}>
              <FilterRowMiddle
                searchKeywords={searchKeywords}
                setSearchKeywords={setSearchKeywords}
                inputValue={inputValue}
                setInputValue={setInputValue}
                suggestions={suggestions}
                selectedDepartments={selectedDepartments}
                setSelectedDepartments={setSelectedDepartments}
                allDepartments={allDepartments}
              />
            </Grid>

            {/* --- MAIN CONTENT: Record List (Left) and Document Content (Right) --- */}
            <Grid item xs={12}>
              <Grid container spacing={2} sx={{ width: '1160px', mx: 'auto' }}>

                {/* LEFT SIDE: Record IDs, Date, Status */}
                <Grid item xs={4}>
                  <RecordListPanel
                    isLoading={isLoading}
                    error={error}
                    paginatedRecords={paginatedRecords}
                    selectedRecordId={selectedRecordId}
                    setSelectedRecordId={setSelectedRecordId}
                    allFilteredRecords={allFilteredRecords}
                    toggleSortDirection={toggleSortDirection}
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    pageCount={pageCount}
                    currentPage={currentPage}
                    handlePageChange={handlePageChange}
                    selectedYear={selectedYear}
                  />
                </Grid>

                {/* RIGHT SIDE: Document Content */}
                <Grid item xs={8}>
                  <RecordContentPanel
                    selectedRecord={selectedRecordContent}
                    isLoading={isContentLoading}
                    error={contentError}
                    selectedRecordContent={selectedRecordContent}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default RecordSearchApp;


/*
          <Grid item xs={12}>
            <Typography variant="body1">
              **Records Found:** {allFilteredRecords.length}
            </Typography>
          </Grid>
*/